// Remove our preload class from the body, preventing early CSS transitions
import '@utils/preload';

// CSS needs to be imported here as it's treated as a module
import '@/css/style.pcss';

// ================================================
// All third-party, globally-required libraries should be loaded here
// ================================================

// Support for lazyload images (and bg images)
import 'lazysizes';
import 'lazysizes/plugins/unveilhooks/ls.unveilhooks';
import 'lazysizes/plugins/bgset/ls.bgset';


// ================================================
// Setup Vue3 - Use only if this project needs Vue!
// ================================================
import {
    createApp,
    nextTick,
    ref,
    provide,
    onMounted,
} from 'vue';
import { DefaultApolloClient } from '@vue/apollo-composable';
import apolloClient from './apollo';

import infiniteScroll from 'vue3-infinite-scroll-good';

import AOS from 'aos';
import 'aos/dist/aos.css';

// Components
import Accordion from './components/Accordion.vue';
import Rot13Email from './components/Rot13Email.vue';
import Projects from './components/Projects.vue';
import ProjectImages from './components/ProjectImages.vue';
import News from './components/News.vue';
import VideoPlayer from './components/VideoPlayer.vue';
import Mapbox from './components/Mapbox.vue';
import SwiperProgress from './components/SwiperProgress.vue';
import Jobs from './components/Jobs.vue';

// Directives
import parallax from './directives/parallax';

// Import Swiper Vue.js components
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Autoplay, Pagination, Scrollbar } from 'swiper';
import CollapseTransition from '@ivanv/vue-collapse-transition/src/CollapseTransition.vue';

import Lenis from 'lenis';
import 'lenis/dist/lenis.css';

const app = createApp({
    delimiters: [ '${', '}' ],

    setup() {
        provide(DefaultApolloClient, apolloClient);

        const swiperModules = ref([ Autoplay, Pagination, Scrollbar ]);

        const menu = ref(null);
        const menuLeft = ref(0);

        const isScrolled = ref(false);
        const onScroll = () => {
            isScrolled.value = window.scrollY > 10;
        };

        window.addEventListener('scroll', onScroll);

        const isSearchOpen = ref(false);
        const search = ref(null);
        const onToggleSearch = async() => {
            isSearchOpen.value = !isSearchOpen.value;

            if (isSearchOpen.value) {
                await nextTick();
                search.value.focus();
            }
        };

        const isMenuOpen = ref(false);
        const onToggleMenu = () => {
            isMenuOpen.value = !isMenuOpen.value;
        };

        const activeMenuNodeId = ref(null);
        const onMenuExpand = async(event, nodeId) => {
            if (window.innerWidth < 1024) {
                return;
            }

            const element = event.target;
            menuLeft.value = element.getBoundingClientRect().left;

            activeMenuNodeId.value = nodeId;
        };

        const onMenuCollapse = () => {
            activeMenuNodeId.value = null;
        };

        const activeSubMenuNodeId = ref(null);
        const onSubMenuExpand = nodeId => {
            if (nodeId === activeSubMenuNodeId.value) {
                activeSubMenuNodeId.value = null;
                return;
            }

            activeSubMenuNodeId.value = nodeId;
        };

        const activeMember = ref(null);

        const jobSearch = ref('');
        const onSearchJobs = () => {
            document.getElementById('open-roles')?.scrollIntoView({ behavior: 'smooth' });
        };

        onMounted(() => {
            setTimeout(() => {
                AOS.init({
                    disable: window.outerWidth < 768,
                    duration: 600,
                });
            }, 300);
        });

        return {
            swiperModules,
            menu,
            menuLeft,
            isScrolled,
            isSearchOpen,
            search,
            onToggleSearch,
            isMenuOpen,
            onToggleMenu,
            activeMenuNodeId,
            onMenuExpand,
            onMenuCollapse,
            activeSubMenuNodeId,
            onSubMenuExpand,
            activeMember,
            jobSearch,
            onSearchJobs,
        };
    },
});

app.component('Swiper', Swiper);
app.component('SwiperSlide', SwiperSlide);
app.component('CollapseTransition', CollapseTransition);
app.component('Accordion', Accordion);
app.component('Rot13Email', Rot13Email);
app.component('Projects', Projects);
app.component('ProjectImages', ProjectImages);
app.component('News', News);
app.component('VideoPlayer', VideoPlayer);
app.component('Mapbox', Mapbox);
app.component('SwiperProgress', SwiperProgress);
app.component('Jobs', Jobs);

app.directive('parallax', parallax);
app.directive('infinite-scroll', infiniteScroll);

app.mount('#app');

new Lenis({
    autoRaf: true,
});
